import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import { getMyInvitations } from '../services/authService';

function SentInvitationsList({ refreshKey }) {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusLabels = {
    pending: { label: t('pages.profile.sentInvitations.statusPending'), className: 'bg-warning text-dark' },
    accepted: { label: t('pages.profile.sentInvitations.statusAccepted'), className: 'bg-success' },
    declined: { label: t('pages.profile.sentInvitations.statusDeclined'), className: 'bg-danger' },
    expired: { label: t('pages.profile.sentInvitations.statusExpired'), className: 'bg-secondary' },
  };

  useEffect(() => {
    const fetchInvitations = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getMyInvitations();
        setInvitations(response.data || []);
      } catch (err) {
        setError(t('pages.profile.sentInvitations.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchInvitations();
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">{t('pages.profile.sentInvitations.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger mb-0">{error}</div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  const getDisplayStatus = (inv) => {
    if (inv.status === 'pending' && inv.isExpired) {
      return statusLabels.expired;
    }
    return statusLabels[inv.status] || { label: inv.status, className: 'bg-secondary' };
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <div>
        <h4 className="mb-3">{t('pages.profile.sentInvitations.title')}</h4>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>{t('pages.profile.sentInvitations.columnEmail')}</th>
                <th>{t('pages.profile.sentInvitations.columnChildren')}</th>
                <th>{t('pages.profile.sentInvitations.columnStatus')}</th>
                <th>{t('pages.profile.sentInvitations.columnSentAt')}</th>
                <th>{t('pages.profile.sentInvitations.columnExpiresAt')}</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => {
                const displayStatus = getDisplayStatus(inv);
                return (
                  <tr key={inv.id}>
                    <td>{inv.invitedEmail}</td>
                    <td>
                      <small>
                        {inv.children?.map((c) => c.name).join(', ') || '-'}
                      </small>
                    </td>
                    <td>
                      <span className={`badge ${displayStatus.className}`}>
                        {displayStatus.label}
                      </span>
                      {inv.status === 'accepted' && inv.acceptedUserName && (
                        <small className="text-muted ms-2">
                          {`(${inv.acceptedUserName})`}
                        </small>
                      )}
                    </td>
                    <td>
                      <small>
                        {inv.createdAt
                          ? new Date(inv.createdAt).toLocaleDateString('hu-HU')
                          : '-'}
                      </small>
                    </td>
                    <td>
                      <small>{inv.expiresAt || '-'}</small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </Paper>
  );
}

SentInvitationsList.propTypes = {
  refreshKey: PropTypes.number,
};

SentInvitationsList.defaultProps = {
  refreshKey: 0,
};

export default SentInvitationsList;
